import { useTranslation } from "react-i18next"

import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@f2e/ui/components/base"
import { SelectItemWithDescription } from "@f2e/ui/components/base-plus"

import type { LLMModel } from "@/hooks/api/llm"

export const SYSTEM_DEFAULT_LLM_MODEL_VALUE = "__system_default__"

interface DefaultModelOption {
  value: string
  label: string
  description?: string
}

interface LLMModelSelectProps {
  models: LLMModel[]
  value?: string
  onValueChange: (value: string) => void
  placeholder: string
  defaultOption?: DefaultModelOption
  testId?: string
  copilotField?: string
  pricingNote?: string
}

const formatModelCredits = (value: number) =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(value)

export const getModelPricingDescription = (
  model: LLMModel,
  pricingTemplate: (input: string, output: string) => string
): string | null => {
  const input = model.inputCreditsPerMillionTokens
  const output = model.outputCreditsPerMillionTokens
  if (!input || !output || input <= 0 || output <= 0) return null

  return pricingTemplate(formatModelCredits(input), formatModelCredits(output))
}

export const LLMModelSelect = ({
  models,
  value,
  onValueChange,
  placeholder,
  defaultOption,
  testId,
  copilotField,
  pricingNote,
}: LLMModelSelectProps) => {
  const { t } = useTranslation()
  const pricingTemplate = (input: string, output: string) =>
    t("workspace.agent.basicForm.llmModelPricing", { input, output })
  const selectedModel = models.find((model) => model.modelId === value)
  const selectedModelPricingDescription = selectedModel
    ? getModelPricingDescription(selectedModel, pricingTemplate)
    : null

  return (
    <>
      <Select data-testid={testId} value={value} onValueChange={onValueChange}>
        <SelectTrigger
          className="w-full"
          data-copilot-field={copilotField}
          data-copilot-value={value}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {defaultOption && (
            <SelectItemWithDescription
              value={defaultOption.value}
              description={defaultOption.description}
            >
              {defaultOption.label}
            </SelectItemWithDescription>
          )}
          {models.map((model) => (
            <SelectItemWithDescription
              key={model.modelId}
              value={model.modelId}
              description={getModelPricingDescription(model, pricingTemplate)}
            >
              {model.displayName}
            </SelectItemWithDescription>
          ))}
        </SelectContent>
      </Select>
      {selectedModelPricingDescription && (
        <p className="text-xs text-muted-foreground">
          {selectedModelPricingDescription}
          {pricingNote && ` · ${pricingNote}`}
        </p>
      )}
    </>
  )
}
